import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';
import type { Control, Path, UseFormClearErrors, UseFormGetValues, UseFormSetError, UseFormSetValue, UseFormUnregister } from 'react-hook-form';
import { useFieldArray } from 'react-hook-form';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { SimpleTableProps } from 'af-components/Controls/SimpleTable/types';
import type { SimpleTableRow } from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import SimpleTableRHFWrapper from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper';
import { setOneErrorToRHF } from 'af-components/RHFFieldWrappers/SimpleTableRHFWrapper/validationRHF';
import type { TableRef } from 'af-components/RHFTableCellComponents/SimpleTable';
import DatePickerCellRenderer from 'af-components/RHFTableCellRenderers/DatePickerCellRenderer';
import TextCell from 'af-components/Table/Cells/TextCell';

import type { CredentialFM, CredentialFormValues, CredentialRHFRow } from './formModel';
import styles from './styles.module.scss';

export type CredentialColumn = keyof CredentialRHFRow;

interface OwnProps {
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	control: Control<CredentialFormValues, any, CredentialFormValues>;
	getValues: UseFormGetValues<CredentialFormValues>;
	unregister: UseFormUnregister<CredentialFormValues>;
	setError: UseFormSetError<CredentialFormValues>;
	clearErrors: UseFormClearErrors<CredentialFormValues>;
	onChange: (credentials: CredentialFM[]) => void;
	setValue: UseFormSetValue<CredentialFormValues>;
}

type Props = OwnProps;

function isCredentialColumn(value: string): value is CredentialColumn {
	return ([
		'rhfId',
		'id',
		'organizationCredentialId',
		'issuedDate',
		'expirationDate',
		'name',
	] as const).includes(value as CredentialColumn);
}

function parseCredentialPath(name: string): Nullable<{ index: number; field: CredentialColumn; }> {
	const parts = name.split('.');
	if (parts.length !== 3) {
		return null;
	}
	if (parts[0] !== 'credentials') {
		return null;
	}

	const index = +parts[1];
	if (!Number.isInteger(index) || index < 0) {
		return null;
	}

	const fieldRaw = parts[2];
	if (!isCredentialColumn(fieldRaw)) {
		return null;
	}

	return { index, field: fieldRaw };
}

function asPath<T>(p: string): Path<T> {
	return p as Path<T>;
}

const EmployeeCredentialForm: React.FC<Props> = (props) => {
	const { control, setError, getValues, unregister, clearErrors, onChange, setValue } = props;

	const tableRef = React.useRef<TableRef>(null);

	const { fields } = useFieldArray<CredentialFormValues, 'credentials', 'rhfId'>({
		control,
		name: 'credentials',
		keyName: 'rhfId',
	});

	const emitToParent = React.useCallback(() => {
		if (!onChange) {
			return;
		}
		const next = (getValues('credentials') ?? []).map(({ ...args }) => ({ ...args, rhfId: undefined }));
		onChange(next);
	}, [getValues, onChange]);

	const validateOnChange = React.useCallback((name: string) => {
		const parsed = parseCredentialPath(name);
		if (!parsed) {
			return;
		}

		const { index, field } = parsed;
		const cellPath = `credentials.${index}` as const;

		const value = getValues(asPath<CredentialFormValues>(cellPath)) as CredentialFM;

		clearErrors(asPath<CredentialFormValues>(cellPath));

		if (!value.expirationDate) {
			setOneErrorToRHF(`${cellPath}.expirationDate`, 'Expiration date has to be after issued date', setError);
			return;
		}

		const issuedDate = value.issuedDate ? TimeUtils.parseDate(value.issuedDate, TimeFormat.DATE_ONLY) : null;
		const expirationDate = TimeUtils.parseDate(value.expirationDate, TimeFormat.DATE_ONLY);

		if (issuedDate && issuedDate > expirationDate) {
			setOneErrorToRHF(`${cellPath}.${field}`, 'Expiration date has to be after issued date', setError);
		}
	}, [getValues, clearErrors, setError]);

	const onCredentialCellChange = React.useCallback(
		(name: string) => {
			const parsed = parseCredentialPath(name);
			if (!parsed) {
				return;
			}

			validateOnChange(name);
			emitToParent();
		},
		[emitToParent, validateOnChange]
	);

	const renderInputCell = React.useCallback(
		(cellName: keyof CredentialRHFRow) =>
			(cell: CellContext<CredentialRHFRow & SimpleTableRow, string>) => {
				return (
					<DatePickerCellRenderer
						control={control}
						dateFormat={TimeFormat.DATE_ONLY}
						fieldsLength={fields.length}
						getValues={getValues}
						name={`credentials.${cell.row.original.index}.${cellName}`}
						onChange={onCredentialCellChange}
						originalDateFormat={TimeFormat.DATE_ONLY}
						placeholderText="N/A"
						rhfId={cell.row.original.rhfId}
						setValue={setValue}
						unregister={unregister}
					/>
				);
			},
		[control, fields.length, getValues, onCredentialCellChange, unregister, setValue]
	);

	const columns: SimpleTableProps<CredentialRHFRow & SimpleTableRow>['columns'] = React.useMemo(() => [
		{
			id: 'name',
			accessor: 'name',
			header: 'Credential Name',
			cell: (_cell: CellContext<CredentialRHFRow & SimpleTableRow, string>) => <TextCell className={styles['name-cell']} value={_cell.getValue()} />,
			enableSorting: true,
		},
		{
			id: 'issuedDate',
			accessor: 'issuedDate',
			header: 'Issued Date *',
			cell: renderInputCell('issuedDate'),
			enableSorting: true,
		},
		{
			id: 'expirationDate',
			accessor: 'expirationDate',
			header: 'Expiration Date *',
			cell: renderInputCell('expirationDate'),
			enableSorting: true,
		},
	], [renderInputCell]);

	const fieldsForTable = React.useMemo(() => {
		return fields.map((f) => {
			const { rhfId, ...rest } = f;
			return { ...rest, id: rhfId, rhfId };
		});
	}, [fields]);

	return (
		<SimpleTableRHFWrapper
			columns={columns}
			fields={fieldsForTable}
			formAccessorName="credentials"
			ref={tableRef}
			titleClassName={styles['table-title-row']}
		/>
	);
};

export default React.memo(EmployeeCredentialForm);
